import React, { useEffect, useRef, useState } from 'react'
import { createOrder, placeOrder } from '../../api/apiHandler';
import { formatDuration } from '../../common/utilityFunctions';
import { showErrorMessage, showSuccessMessage } from '../../common/notification';
import { Loading } from '../../components/loading/loading';
import { setLocalStorageItem } from '../../common/LocalStorageService';
import { useDispatch, useSelector } from 'react-redux';
import { addRemoveDiscountCode, fetchCartData, removeFromCart } from '../../slices/cartPaymentSlice';
import { useTranslation } from 'react-i18next';
import '../../i18';
import PaymentSuccessModal from '../../modals/paymentSucessModal/paymentSuccessModal';

export const Cart = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [hasMounted, setHasMounted] = useState(false);
    const userInfo = useSelector((state) => state.auth.userInfo);
    const [wantReload, setWantReload] = useState(false);
    const [isCodeApplied, setIsCodeApplied] = useState(null);
    const [coursePrice, setCoursePrice] = useState(0.00);
    const [discount, setDiscount] = useState(0.00);
    const [taxAmount, setTaxAmount] = useState(2.30);
    const [totalAmount, setTotalAmount] = useState(0.00);
    const cartDaata = useSelector((state) => state.cart.cartData);
    const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] = useState(false);
    const dispatch = useDispatch();

    // Ref to capture the latest isCodeApplied state
    const isCodeAppliedRef = useRef(isCodeApplied);

    useEffect(() => {
        isCodeAppliedRef.current = isCodeApplied;
    }, [isCodeApplied]);

    useEffect(() => {
        if (cartDaata) {
            let coursePrice = cartDaata?.cart_details?.data?.reduce((sum, course) => sum + parseFloat(course?.cart_course?.price) || 0, 0);
            coursePrice = parseFloat(coursePrice?.toFixed(2)); // Format to 2 decimal places after calculation

            let discount = parseFloat(cartDaata?.discount) || 0.00;
            let taxAmount = parseFloat(cartDaata?.pricing?.total_tax);
            let totalAmount = coursePrice - discount; // Total amount calculation

            setCoursePrice(coursePrice);
            setTaxAmount(taxAmount);
            setDiscount(discount);
            setTotalAmount(parseFloat(totalAmount?.toFixed(2))); // Format totalAmount to 2 decimal places

            if (cartDaata.code !== null && cartDaata.code !== undefined && cartDaata.code !== "") {
                setIsCodeApplied(cartDaata.code);
            } else {
                setIsCodeApplied(null);
            }
        }
    }, [cartDaata]);

    useEffect(() => {
        if (!hasMounted) setIsLoading(true);
        dispatch(fetchCartData())
            .then(response => {
                if (!hasMounted) setIsLoading(false);
                setHasMounted(true);
            });

        return () => {
            deleteDiscount();
        }
    }, [wantReload]);

    useEffect(() => {
        const handleStorageChange = (event) => {

            if (event.key === 'discountCode') {
                setIsCodeApplied(event.newValue || null);
                setWantReload(prevValue => !prevValue);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    useEffect(() => {
        if (isCodeApplied) {
            setLocalStorageItem('discountCode', isCodeApplied);
        } else {
            localStorage.removeItem('discountCode');
        }
    }, [isCodeApplied]);

    const handleBeforeUnload = () => {
        if (isCodeApplied) {
            localStorage.removeItem('discountCode');
            deleteDiscount(cartDaata.id);
        }
    };

    useEffect(() => {
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [isCodeApplied, cartDaata]);

    const deleteDiscount = async () => {
        try {
            if (isCodeAppliedRef.current) {
                dispatch(
                    addRemoveDiscountCode({
                        payload: {
                            cart_id: cartDaata?.id,
                            code: isCodeAppliedRef.current,
                            is_remove: true
                        }
                    })
                )
                    .then(response => {
                        if (response.payload.code === 200) {
                            setIsCodeApplied(null);
                        }
                    });
            }
        }
        catch (err) {
            console.log('err: ', err);
        }
    }

    const handleDeleteCartCourse = (course_id) => {
        dispatch(
            removeFromCart({
                payload: {
                    cart_id: cartDaata?.id,
                    course_id
                }
            })
        )
            .then(async (response) => {
                if (response.payload.code === 200) {
                    await deleteDiscount();
                    const discountInp = document.getElementById("discountInp");
                    if (discountInp)
                        discountInp.value = '';
                    showSuccessMessage(response.payload.message);
                }
            });
    }

    const handleApplyDiscount = () => {
        try {
            const discountInp = document.getElementById("discountInp");
            const code = discountInp.value.trim();
            if (code !== '') {
                dispatch(
                    addRemoveDiscountCode({
                        payload: {
                            cart_id: cartDaata?.id,
                            code: code,
                            is_remove: isCodeAppliedRef.current ? true : false
                        }
                    })
                )
                    .then(response => {
                        if (response.payload.code === 200) {
                            if (isCodeAppliedRef.current) {
                                discountInp.value = '';
                                setIsCodeApplied(null);
                            }
                            showSuccessMessage(response.payload.message);
                        }
                        else {
                            showErrorMessage(response.payload.message);
                        }
                    });
            }
        }
        catch (err) {
            console.log('err: ', err);
        }
    }

    function loadScript(src) {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = src;
            script.onload = () => {
                resolve(true);
            };
            script.onerror = () => {
                resolve(false);
            };
            document.body.appendChild(script);
        });
    }

    async function displayRazorpay() {
        const res = await loadScript(
            "https://checkout.razorpay.com/v1/checkout.js"
        );

        if (!res) {
            alert(t("razorPayFailed"));
            return;
        }

        // creating a new order
        const result = await createOrder({
            total_amount: totalAmount,
            currency: "INR"
        });

        if (!result) {
            alert(t("serverError"));
            return;
        }

        if (result.code === 200) {

            // Getting the order details back
            const { amount, order_id, currency } = result.data;

            const options = {
                key: result.data.razorpay_api_key, // Enter the Key ID generated from the Dashboard
                amount: amount.toString(),
                currency: currency,
                name: "Guarding.Tech",
                description: "Test Transaction",
                // image: { logo },
                order_id: order_id,
                handler: async function (response) {
                    const data = {
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_signature: response.razorpay_signature,
                        currency,
                        amount,
                        tax: taxAmount,
                        cart_id: cartDaata.id,
                        cart_details: cartDaata.cart_details.data.map(course_details => ({ courseId: course_details.course_id })),
                        discount_code: isCodeApplied,
                        discount: discount,
                        sub_total: coursePrice
                    };

                    const result = await placeOrder({
                        ...data
                    });

                    if (result.code === 200) {
                        // showSuccessMessage(result.message);
                        setIsPaymentSuccessModalOpen(true);
                    }
                    else {
                        showErrorMessage(result.message);
                    }
                    setWantReload(prevValue => !prevValue);
                },
                prefill: {
                    name: userInfo.full_name,
                    email: userInfo.email,
                    contact: userInfo.country_code + userInfo.phone_number,
                },
                notes: {
                    address: userInfo.user_city + userInfo.user_country,
                },
                theme: {
                    color: "#61dafb",
                },
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        }
    }

    if (isLoading) {
        return (
            <Loading />
        )
    }

    return (
        <div className="course-single-area pd-top-120 pd-bottom-90">
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <div className="PageTitleBox">
                            <h2 className="PageTitleHeading">{t('cartHeading')}</h2>
                        </div>
                    </div>
                    <div className="col-xxl-8 col-xl-8 col-lg-8 col-md-12 col-sm-12 col-12">
                        <div className="row">
                            {
                                cartDaata && cartDaata.cart_details && cartDaata.cart_details.data?.length > 0 &&
                                cartDaata.cart_details.data.map(course => (
                                    <div className="col-xxl-6 col-xl-6 col-lg-6  col-md-6 col-md-6 col-sm-12 col-12 CourseGride" key={course?.id}>
                                        <div className="single-course-inner">
                                            <div className="thumb">
                                                <img src={course?.cart_course?.image || process.env.PUBLIC_URL + "/assets/img/course/2.png"} alt="img" />
                                            </div>
                                            <div className="details">
                                                <div className="details-inner">
                                                    <div className="d-flex justify-content-between mb-3">
                                                        <p className="CardMainHeading">{course?.cart_course?.course_category?.name}</p>
                                                        {
                                                            course?.cart_course?.assign_trainer && course?.cart_course?.assign_trainer?.length > 0 &&
                                                            <div className="emt-user">
                                                                <span className="u-thumb">
                                                                    <img src={course?.cart_course?.assign_trainer[0]?.trainer_details?.image || process.env.PUBLIC_URL + '/assets/img/testimonial/review1.png'} alt="img" />
                                                                </span>
                                                                <span className="align-self-center">{course?.cart_course?.assign_trainer[0]?.trainer_details?.full_name}</span>
                                                            </div>
                                                        }
                                                    </div>
                                                    <h6 className="CardMainSubHeading">{course?.cart_course?.name}</h6>
                                                    <div className="LassonsMain">
                                                        <ul className="LassonsUl">
                                                            <li className="LassonsLi">
                                                                <p className="Lessonstext">{course?.cart_course?.total_lession} {t('lessons')}</p>
                                                            </li>
                                                            <li className="dots" />
                                                            <li className="LassonsLi">
                                                                <p className="Lessonstext">{formatDuration(course?.cart_course?.duration, t)}</p>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                                <div className="emt-course-meta">
                                                    <div className="LassonsCardFooter">
                                                        <ul className="LassonsCardFooterUl">
                                                            <li className="LassonsCardFooterLi">
                                                                <div className="rating">
                                                                    <i className="fa fa-star" /> {course?.cart_course?.avg_review}
                                                                    <span>({course?.cart_course?.total_review})</span>
                                                                </div>
                                                            </li>
                                                            <li className="LassonsCardFooterLi">
                                                                <div className="price">
                                                                    <span>₹{course?.cart_course?.price}</span>
                                                                </div>
                                                            </li>
                                                            <li className="LassonsCardFooterLi">
                                                                <div className="price">
                                                                    <i className="fa-solid fa-trash" onClick={() => handleDeleteCartCourse(course?.cart_course?.id)} style={{ cursor: "pointer" }} />
                                                                </div>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-12 col-sm-12 col-12">
                        <div className="row">
                            {/* <div class="col-12">
                              <div class="CheckoutBox">
                                  <a class="btn btn-base b-animate-3 mr-sm-3 mr-2" href="javascript:void(0)">
                                      Checkout
                                  </a>
                              </div>
                          </div> */}
                            {cartDaata?.cart_details?.data?.length > 0 &&
                                <div className="col-12">
                                    <div className="row">
                                        <div className="col-12 PaymentMathodGride">
                                            <div className="PaymentMathodMainBox">
                                                <div className="PaymentMathodBox">
                                                    <ul className="std-price-ul">
                                                        <li>
                                                            <div className="EnterDiscountCodeBox">
                                                                <div className="single-input-inner style-bg-border position-relative">
                                                                    <input
                                                                        defaultValue={cartDaata?.code || ''}
                                                                        disabled={isCodeApplied}
                                                                        id='discountInp'
                                                                        type="text"
                                                                        placeholder={t("enterFieldName", { field: t("Discount_Code") })}
                                                                    />
                                                                    <span
                                                                        onClick={handleApplyDiscount}
                                                                        className="EnterDiscountCodeLink"
                                                                        id="togglePassword"
                                                                        style={{ cursor: "pointer" }}
                                                                    >
                                                                        <img
                                                                            src={`${process.env.PUBLIC_URL}${isCodeApplied ? "/assets/img/icon/crossIcon.svg" : "/assets/img/icon/plusIcon.svg"}`}
                                                                            alt="plusIcon"
                                                                            className="img-cover"
                                                                        />
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </li>
                                                        <li className="std-price-li">
                                                            <p className="std-left-lable">{t('coursePrice') + ' (Tax included)'} :</p>
                                                            <p className="std-left-price">₹{parseFloat(coursePrice).toFixed(2)}</p>
                                                        </li>
                                                        <li className="std-price-li">
                                                            <p className="std-left-lable">{t('tax') + ` (${cartDaata?.pricing?.tax_percentage}%)`} :</p>
                                                            <p className="std-left-price">₹{taxAmount.toFixed(2)}</p>
                                                        </li>
                                                        <li className="std-price-li">
                                                            <p className="std-left-lable">{t('discount')} :</p>
                                                            <p className="std-left-price">₹{discount.toFixed(2)}</p>
                                                        </li>
                                                        <li className="std-price-li">
                                                            <p className="std-left-lable">{t('total')} :</p>
                                                            <p className="std-left-price">₹{totalAmount.toFixed(2)}</p>
                                                        </li>
                                                        <button className="btn btn-base btn-radius" onClick={() => displayRazorpay()} disabled={!cartDaata || !cartDaata.cart_details || cartDaata.cart_details.data?.length === 0}>
                                                            {t('buyNow')}
                                                        </button>
                                                    </ul>

                                                </div>
                                            </div>
                                        </div>
                                        {/* <div className="col-12 PaymentMathodGride">
                                        <div className="PaymentMethodCardMain">
                                            <div className="PaymentMethodCard">
                                                <ul className="PaymentMethodCardUl">
                                                    <li className="PaymentMethodCardLi">
                                                        <h5>Payment Method</h5>
                                                    </li>
                                                    <li className="PaymentMethodCardLi">
                                                        <label className="PayCardBox" htmlFor="Radio1">
                                                            <ul className="PayCardBoxUl">
                                                                <li className="PayCardBoxLiImgBox">
                                                                    <img
                                                                        src={process.env.PUBLIC_URL + "/assets/img/icon/visa.svg"}
                                                                        alt="visa"
                                                                        className="img-contain"
                                                                    />
                                                                </li>
                                                                <li className="PayCardBoxLiNumber">
                                                                    **** **** 3723
                                                                </li>
                                                                <li className="PayCardBoxLiRadio">
                                                                    <input
                                                                        type="radio"
                                                                        id="Radio1"
                                                                        name="card"
                                                                        className=""
                                                                    />
                                                                </li>
                                                            </ul>
                                                        </label>
                                                    </li>
                                                    <li className="PaymentMethodCardLi">
                                                        <label className="PayCardBox" htmlFor="Radio2">
                                                            <ul className="PayCardBoxUl">
                                                                <li className="PayCardBoxLiImgBox">
                                                                    <img
                                                                        src={process.env.PUBLIC_URL + "/assets/img/icon/mastercard_logo.svg"}
                                                                        alt="mastercard_logo"
                                                                        className="img-contain"
                                                                    />
                                                                </li>
                                                                <li className="PayCardBoxLiNumber">
                                                                    **** **** 5625
                                                                </li>
                                                                <li className="PayCardBoxLiRadio">
                                                                    <input
                                                                        type="radio"
                                                                        id="Radio2"
                                                                        name="card"
                                                                        className=""
                                                                    />
                                                                </li>
                                                            </ul>
                                                        </label>
                                                    </li>
                                                    <li className="AddThisCardLi">
                                                        <div className="row">
                                                            <div className="col-12">
                                                                <label htmlFor="field1" className="form-label">
                                                                    Card Number{" "}
                                                                    <span className="payment-required-star">*</span>
                                                                </label>
                                                                <div className="single-input-inner style-bg-border position-relative">
                                                                    <input
                                                                        type="number"
                                                                        className="form-control apply-input"
                                                                        placeholder="xxxx xxxx xxxx xxxx"
                                                                        id="field1"
                                                                        name="field1"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-xxl-6 col-xl-6 col-lg-6  col-md-6 col-sm-12 col-12">
                                                                <label htmlFor="field1" className="form-label">
                                                                    Expiration date{" "}
                                                                    <span className="payment-required-star">*</span>
                                                                </label>
                                                                <div className="single-input-inner style-bg-border position-relative">
                                                                    <div className="input-group mb-3">
                                                                        <input
                                                                            type="text"
                                                                            className="form-control apply-input"
                                                                            placeholder="Month"
                                                                            aria-label="Username"
                                                                        />
                                                                        <input
                                                                            type="text"
                                                                            className="form-control apply-input"
                                                                            placeholder="Year"
                                                                            aria-label="Server"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="col-xxl-6 col-xl-6 col-lg-6  col-md-6 col-sm-12 col-12">
                                                                <label htmlFor="field1" className="form-label">
                                                                    CVV / CVC{" "}
                                                                    <span className="payment-required-star">*</span>
                                                                </label>
                                                                <div className="single-input-inner style-bg-border position-relative">
                                                                    <input
                                                                        type="number"
                                                                        className="form-control apply-input"
                                                                        placeholder="xxx"
                                                                        id="field1"
                                                                        name="field1"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-12">
                                                                <label htmlFor="field1" className="form-label">
                                                                    Name on Card{" "}
                                                                    <span className="payment-required-star">*</span>
                                                                </label>
                                                                <div className="single-input-inner style-bg-border position-relative">
                                                                    <input
                                                                        type="text"
                                                                        className="form-control apply-input"
                                                                        placeholder="Enter Card Holder Name"
                                                                        id="field1"
                                                                        name="field1"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="col-12">
                                                                <div className="SaveThisCardBox">
                                                                    <input type="checkbox" id="SaveThiscard" />
                                                                    <label htmlFor="SaveThiscard">
                                                                        Save This Card
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </li>
                                                    <li className="PaymentMethodCardLiLink">
                                                        <div className="btnBox">
                                                            <a
                                                                className="btn btn-base b-animate-3 AddPaymentMethodLink"
                                                                href="javascript:void(0)"
                                                            >
                                                                Add Payment Method
                                                            </a>
                                                        </div>
                                                        <div className="btnBox">
                                                            <a
                                                                className="btn btn-base b-animate-3"
                                                                href="javascript:void(0)"
                                                                data-bs-toggle="modal"
                                                                data-bs-target="#PaymentSuccessModal"
                                                            >
                                                                Apply
                                                            </a>
                                                        </div>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div> */}
                                    </div>
                                </div>
                            }
                            <PaymentSuccessModal
                                isOpen={isPaymentSuccessModalOpen}
                                isClose={() => setIsPaymentSuccessModalOpen(false)}
                            />
                        </div>
                    </div>
                </div>
            </div>
            {
                (!cartDaata || !cartDaata?.cart_details || !cartDaata?.cart_details?.data || cartDaata?.cart_details?.data?.length === 0) && (
                    <h4 className='text-center mt-3 fw-light'>{t('noCourses')}</h4>
                )
            }
        </div>
    )
}
